import React, {useEffect, useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {RadioButton} from "primereact/radiobutton";
import {Bar} from "react-chartjs-2";

import {
    formatCurrency,
    isPriceNotListed,
    formatCurrencyText, getDatePrice,
    getHostName,
    canEnterManualPrices, getKeyValue, getLastPrice, isAdmin, isCampaignManufacturer, isEfsElektronikUser, isPreventSelect, isSameProductUrl, isShippingPricesHandledSeparately,
    stripTrackingParams, urlValidator, user
} from "../../utils/Utils";
import {toDateString, toDateTimeString, getWeeksAgo, toDateStringIgnoringTimezone} from "../../utils/TimeUtil";

import {OverlayPanel} from "primereact/overlaypanel";
import ScrapeStatus from "./ScrapeStatus";
import {InputSwitch} from "primereact/inputswitch";
import {StockTemplate, IndexTemplate} from "../../utils/InlineComponents";
import {isCampaignInactiveSeller, isOwnRowCampaignInactive} from "./campaignInactiveSeller";
import {AppContext} from "../../HomePage";
import {Sidebar} from "primereact/sidebar";
import {InputNumber} from "primereact/inputnumber";
import {CampaignContext} from "../../App";
import CompetitorBulkOperations from "./CompetitorBulkOperations";
import CompetitorViolationAction from "./CompetitorViolationAction";
import {Tag} from "primereact/tag";
import {ProductService} from "../../service/ProductService";
import {Dropdown} from "primereact/dropdown";
import {UserService} from "../../service/UserService";
import {Calendar} from "primereact/calendar";
import {normalizeMarketplaceKey, readMarketplaceOperator} from "../Campaign/marketplaceOperatorSettings";
import {groupSellerRows, initialCollapsedKeys, formatDelivery, isDeliveryTracked} from "./marketplace/marketplaceProfile";
import {marketplaceLabel, ownAwareMarketplaceLabel, operatorTextOptions, OWN_STORES_KEY} from "./marketplace/marketplaceProfile";

// The multiplier applied to a competitor can be configured on the URL, on its
// seller or on its site -- the API sends the resolved one as `multiplier` and
// this URL's own as `url_multiplier`. When the URL has none, the resolved value
// is inherited and belongs in a placeholder, not in the input.
const inheritedMultiplier = (competitor) =>
    (competitor.url_multiplier === null || competitor.url_multiplier === undefined)
        ? competitor.multiplier : null;

const Competitor = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const {campaigns} = React.useContext(CampaignContext);

    const op = useRef(null);
    const actionsOp = useRef(null);

    let selectedProduct = {...props.selectedProduct};
    let selectedCampaign = {...campaigns.find(c=> c.id===props.selectedProduct.campaign_id)};
    // Buybox (Marketplace API) campaigns source competitors from the
    // marketplace's API only -- a manual seller URL is meaningless there. Same
    // settings.marketplace_api presence check as Product.js's isBuyboxCampaign
    // / Campaign.js's isMarketplaceBuybox.
    const isBuyboxCampaign = !!selectedCampaign?.settings?.marketplace_api;
    // Marketplace operator profile: sellers are grouped by marketplace (see
    // groupSellerRows). Every other profile renders the table as before.
    const isMarketplaceProfile = props.profile?.key === 'marketplace';
    const marketplaceConfig = isMarketplaceProfile ? readMarketplaceOperator(selectedCampaign?.settings) : null;
    const ownMarketplaceKey = normalizeMarketplaceKey(marketplaceConfig?.own_marketplace_key ?? '');

    const [product_url, setProductUrl] = useState("");
    const [formErrors, setFormErrors] = useState({
        product_url: "",
    });
    const [expandedCompetitor, setExpandedCompetitor] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddPriceModal, setShowAddPriceModal] = useState(false);
    // "Add Seller" with a price typed by hand, for a seller that publishes no price online.
    const EMPTY_MANUAL_SELLER = {enabled: false, price: null, currency: null, in_stock: true, product_name: ''};
    const [manualSeller, setManualSeller] = useState(EMPTY_MANUAL_SELLER);
    // The currency of the product's own price, the natural default for a typed competitor price.
    const ownCurrency = () => {
        const own = (props.selectedProduct?.competitors || []).find(c => c.is_mine);
        return getLastPrice(own?.prices)?.currency || 'EUR';
    };
    const [currencies, setCurrencies] = useState([]);
    const [date, setDate] = useState(toDateStringIgnoringTimezone(new Date()));
    const [showViolationActionModal, setShowViolationActionModal] = useState(false);

    const [selectedCompetitor, setSelectedCompetitor] = useState(null);
    const [newPrice, setNewPrice] = useState(null);

    const [showVariants, setShowVariants] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [showBulkOperations, setShowBulkOperations] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    // 'listing' removes the marketplace listing together with every seller row it
    // feeds; 'seller' deactivates just the clicked row instead (see getDeleteFamily).
    const [deleteMode, setDeleteMode] = useState('listing');
    const [sellerFilters, setSellerFilters] = useState(['in_stock', 'out_of_stock']);
    // Tracks manual chip interaction; while untouched the default selection is
    // recomputed per product (see the effect below sellerFilterCount).
    const sellerFiltersTouched = useRef(false);
    // Brand-only filter dimensions, AND-ed with the stock filter (product-list format).
    const [violationFilter, setViolationFilter] = useState(null);    // null | 'map' | 'mrp' | 'none'
    const [unauthorizedOnly, setUnauthorizedOnly] = useState(null);  // null | true
    // Marketplace groups the user opened. Every group, the own one included, starts
    // collapsed (header only); collapsedKeys below is derived from this so groups
    // that appear after the competitors load start collapsed too.
    // A deep link (highlightCompetitorUrl) starts with the highlighted seller's group open.
    const seedExpandedKeys = () => {
        if (!isMarketplaceProfile) return new Set();
        const rows = props.selectedProduct?.competitors || [];
        const collapsed = initialCollapsedKeys(rows, marketplaceConfig, props.highlightCompetitorUrl);
        const expanded = new Set();
        groupSellerRows(rows, marketplaceConfig, new Set()).forEach(r => {
            if (!collapsed.has(r.mpGroupKey)) expanded.add(r.mpGroupKey);
        });
        return expanded;
    };
    const [mpExpandedKeys, setMpExpandedKeys] = useState(seedExpandedKeys);
    const mpSeedKey = useRef(`${props.selectedProduct?.id}|${props.highlightCompetitorUrl || ''}`);

    const productService = new ProductService();
    const userService = new UserService();

    useEffect ( ()=>{
        selectedProduct = {...props.selectedProduct};
        selectedCampaign = {...campaigns.find(c=> c.id===props.selectedProduct.campaign_id)};
    },[props.selectedProduct]);

    // A different product (or deep link) starts with its own initial group state again.
    useEffect(() => {
        const seedKey = `${props.selectedProduct?.id}|${props.highlightCompetitorUrl || ''}`;
        if (mpSeedKey.current === seedKey) return;
        mpSeedKey.current = seedKey;
        setMpExpandedKeys(seedExpandedKeys());
    }, [props.selectedProduct?.id, props.highlightCompetitorUrl]);

    const getCurrencies = (rowData) => {

        setSelectedCompetitor(rowData);
        let newPrice = {
            "competitor_url": rowData.product_url,
            "added_price" : 0,
            "added_sale_price" : 0,
            "added_stock" : 1,
            "added_currency" : null,
            "added_date": new Date(),
            "pause_automatic_check": !rowData.is_mine
        };

        if (rowData?.prices.length > 0) {
            let lastPrice = rowData.prices[rowData.prices.length - 1];

            newPrice = {
                "competitor_url": rowData.product_url,
                "added_price" : lastPrice?.value,
                "added_sale_price" : lastPrice?.sale_value,
                "added_stock" : lastPrice?.stock,
                "added_currency" : lastPrice?.currency,
                "added_date": new Date(),
                "pause_automatic_check": !rowData.is_mine
            };

        }
        setNewPrice({...newPrice});

        if (currencies.length === 0) {
            userService.getCurrencies().then(data=>{
                setCurrencies(data);
            });
        }
        setShowAddPriceModal(true);
    };

    const selectDate = (e) => {
        setDate(toDateStringIgnoringTimezone(e.value));
    }

    const addDialog = () => {
        // Buybox campaigns have no manual add-competitor entry point -- guard
        // the opener itself so this can't be triggered via the hidden button
        // below or the actionsRef.openAdd handle exposed to the parent.
        if (isBuyboxCampaign) return;
        setDialogVisible(true);
        setProductUrl("");
        setFormErrors({product_url: ""});
    };

    // Expose add/bulk actions so the parent can trigger them from the sticky
    // header next to the view selector.
    if (props.actionsRef)
        props.actionsRef.current = {
            openAdd: addDialog,
            openBulk: () => setShowBulkOperations(true),
        };

    const addCompetitor = () => {
        // Validate on submit so the panel opens clean (no upfront error).
        const error = urlValidator(product_url, t);
        if (error) {
            setFormErrors({product_url: error});
            return;
        }
        if (manualSeller.enabled && !(manualSeller.price > 0)) {
            setFormErrors({product_url: t('product_detail.manual_price_required')});
            return;
        }
        const competitor = manualSeller.enabled
            ? {product_url: product_url, product_name: manualSeller.product_name,
               manual_price: {added_price: manualSeller.price, added_currency: manualSeller.currency || ownCurrency(),
                              added_stock: manualSeller.in_stock ? 1 : 0}}
            : {product_url: product_url};
        props.addCompetitor([competitor], {product_url: ""});
        setDialogVisible(false);
        setProductUrl("");
        setManualSeller(EMPTY_MANUAL_SELLER);
    };
    const changeIsDynamicPricingActive = ( rowData) => {
        const product_urls = [];
        product_urls.push(rowData.product_url);
        props.changeCompetitorIsDynamicPricingActive(product_urls, rowData.is_dynamic_pricing_active);
    };
    const changeIsActive = (rowData) => {
        const product_urls = [];
        product_urls.push(rowData.product_url);
        props.changeCompetitorIsActive(product_urls, rowData.is_active);
    };
    // Rows that have to go together with `competitor`.
    //
    // A marketplace seller row is synthetic: the spider repopulates it from its
    // listing page on every price check, so removing the row alone only wipes its
    // price history and the next run brings it straight back. The listing row and
    // its other seller rows therefore belong to the same delete.
    //
    // This reads selectedProduct.competitors rather than the displayed list on
    // purpose: listing rows are hidden from non-admins, and leaving the listing
    // behind is exactly what made their deletions come back. parent_url is not
    // trusted on its own either -- when a product tracks two listings of the same
    // site, the scraper stamps every seller row of that site with whichever
    // listing it parsed last -- so the row's own URL decides membership too.
    const getDeleteFamily = (competitor) => {
        if (!competitor)
            return [];
        const anchors = [competitor.product_url, competitor.parent_url].filter(url => url);
        return (selectedProduct.competitors || []).filter(c => c.product_url && anchors.some(anchor =>
            c.product_url === anchor || c.parent_url === anchor || isSameProductUrl(c.product_url, anchor)));
    };

    const closeDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setSelectedCompetitor(null);
        setDeleteMode('listing');
    };

    const deleteCompetitor = () => {
        const product_urls = getDeleteFamily(selectedCompetitor).map(p => p.product_url);
        props.deleteCompetitor(product_urls).then(closeDeleteConfirmation);
    };

    // The seller row stays in the list as passive with no stock, which is what
    // keeps its listing from recreating it on the next price check.
    const deactivateCompetitorOnly = () => {
        Promise.resolve(props.changeCompetitorIsActive([selectedCompetitor.product_url], true))
            .then(closeDeleteConfirmation);
    };
    const updateCompetitor = () => {
        // Drop ad click ids (srsltid, gclid, utm_*, ...) so the stored URL stays clean.
        const newUrl = stripTrackingParams(selectedCompetitor.product_url);
        // competitor_url is the original key used to locate the competitor;
        // product_url carries the (possibly changed) new URL to write.
        let data = {"competitor_url": selectedCompetitor._original_url || selectedCompetitor.product_url,
            "product_url": newUrl,
            "multiplier": selectedCompetitor.url_multiplier,
            "discount_type": selectedCompetitor.discount_type,
            "manual_shipping_price": selectedCompetitor.manual_shipping_price,
            "manual_shipping_price_threshold": selectedCompetitor.manual_shipping_price_threshold}
        props.updateCompetitor(data);
        setShowEditModal(false);
        setSelectedCompetitor(null);
    };
    const addCompetitorPrice = () => {
        productService.addCompetitorPrice(selectedProduct.id, newPrice).then((data) => {
            setShowAddPriceModal(false);
            selectedCompetitor.prices.push(data);
            setSelectedCompetitor({...selectedCompetitor});
            toast.current.show({severity: 'success', summary: t('product_detail.edit_competitor'), detail: t('message_detail.successful')});
        }).catch(error =>{
            setShowAddPriceModal(false);
            setError(error);
            setError(null);
        });

    };

    const deleteCompetitorPrice = (rowData, competitor_url) => {
        let data = {"competitor_url": competitor_url, "created_at": rowData.created_at}
        productService.deleteCompetitorPrice(selectedProduct.id, data).then(() => {
            toast.current.show({severity: 'success', summary: t('product_detail.edit_competitor'), detail: t('message_detail.successful')});
        }).catch(error =>{
            setError(error);
            setError(null);
        });

    };
    const matchVariant = (e) => {
        let variantData = e.value;
        props.matchCompetitorVariant(selectedCompetitor.product_url, variantData.properties);
        setTimeout( () => {
            setShowVariants(false);
        }, 1000);
    };

    const actionTemplate = (rowData, props) => {
        // Customers with manual prices enabled may delete only the prices they typed in.
        if (!isAdmin() && !rowData.is_manual) return null;
        return <React.Fragment>
            <Button icon="pi pi-trash" className="p-button-outlined p-button-rounded p-button-danger p-mr-1"
                    tooltip={t('actions.remove')} tooltipOptions={{position: 'bottom'}} onClick={() => {deleteCompetitorPrice(rowData, props.field);}}/>
        </React.Fragment> ;
    };

    const competitorRowExpansionTemplate = (rowData) => {

        const c = selectedCampaign?.competitors?.filter(c=> getHostName(c.url).includes(getHostName(rowData.product_url)))

        return (
            <div className="p-grid p-fluid" style={{"margin":"1em 0em"}}>

                {c && c.length > 0 && <div className="p-grid p-card p-col-12 p-nogutter" >
                    <div className="p-col-3 p-text-bold">{t('campaign.competitors.email')}</div>
                    <div className="p-col-3">{c[0].email}</div>
                    <div className="p-col-3 p-text-bold">{t('campaign.competitors.phone')}</div>
                    <div className="p-col-3">{c[0].phone}</div>
                    <div className="p-col-3 p-text-bold">{t('campaign.competitors.address')}</div>
                    <div className="p-col-3">{c[0].address}</div>
                    <div className="p-col-3 p-text-bold">{t('campaign.competitors.note')}</div>
                    <div className="p-col-3">{c[0].note}</div>
                </div>
                }

                <div className="p-card p-col-12 p-mt-1">
                    <Bar id="comparison" data={competitorChartData(rowData)} options={competitorChartOptions()} height={200}/>
                </div>

                <div className="p-card p-col-12 p-mt-1">
                    <div className="p-grid p-col-12 p-nogutter" >
                        <div className="p-col-11 p-text-bold"><h5>{t('product_detail.last_checks')}</h5></div>
                        {(canEnterManualPrices() || (isEfsElektronikUser() && rowData.is_mine)) && <div className="p-col-1" style={{textAlign: 'right'}}><Button icon="pi pi-plus" data-tour="step12" className="p-button-outlined p-button-rounded p-button-success p-mr-1" tooltipOptions={{position: 'bottom'}} tooltip={t('actions.add_price')} onClick={() => {getCurrencies(rowData); }}/></div>}
                    </div>
                    <DataTable value={rowData.prices}
                               paginator={true}
                               rows={5}
                               responsive={true}
                               sortField="created_at" sortOrder={-1}>
                        <Column field="created_at" header={t('product_detail.date')} body={priceDateTemplate} />
                        <Column field="value" header={t('product_detail.price')} headerStyle={{width:'15em'}} body={priceValueTemplate}/>
                        <Column header={t('product.list_price')} headerStyle={{textAlign:"center", width:'10%'}} field="sale_value" body={priceSaleValueTemplate}/>
                        {showDiscountRate() && <Column header={t('product.discount_rate')} headerStyle={{textAlign:"center", width:'10%'}} field="discount_rate" body={priceDiscountRateTemplate}/>}
                        <Column field="stock" header={t('product_detail.stock')} headerStyle={{width:'8em'}} body={priceStockTemplate}/>
                        {canEnterManualPrices() && <Column body={actionTemplate} field={rowData.product_url} style={{textAlign: 'center', width:"13em"}} />}
                    </DataTable>
                </div>
            </div>);
    };
    const competitorChartData = (rowData) => {
        let startDate = rowData.prices && rowData.prices.length > 0 ? new Date(rowData.prices[0].created_at) : new Date(getWeeksAgo(2));
        let endDate = new Date();
        let days = [];

        let dataCompetitor = [];
        let dataCompetitorStock = [];
        if (rowData) {

            while (startDate < endDate)
            {
                const endDateString = toDateString(endDate);
                days.unshift(endDateString);

                let price = getDatePrice(rowData.prices, endDateString);

                if (price && price.value) {
                    dataCompetitor.push({x: endDateString, y: parseFloat(price.value), list_price: parseFloat(price.sale_value), discount_rate: parseFloat(price.discount_rate), currency: price.currency});
                }
                else {
                    dataCompetitor.push({x: endDateString});
                }

                if (price) {
                    dataCompetitorStock.push({x:endDateString, y:(price.stock)});
                }
                else {
                    dataCompetitorStock.push({x:endDateString});
                }

                endDate.setDate(endDate.getDate() - 1);
            }
        }

        let dataset = [ {
            type: 'line',
            label: t('product_detail.price'),
            borderColor: "red",
            borderWidth: 2,
            fill: false,
            data: dataCompetitor,
            lineTension: 0,
            yAxisID:'y',
        },
            {
                type: 'line',
                label: t('product_detail.stock'),
                borderColor: "green",
                borderWidth: 2,
                fill: false,
                data: dataCompetitorStock,
                lineTension: 0,
                yAxisID:'y1',
            }];


        return {
            labels: days,
            datasets: dataset
        };
    };

    const competitorChartOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            legend:{
                labels: {
                    filter: function(legendItem) {
                        return  !(legendItem.datasetIndex === 1);
                    }
                }
            },
            scales: {
                y: {
                    display: true,
                    ticks: {
                        fontSize: 10
                    }
                },
                y1: {
                    display: true,
                    grid:{
                        display: false,
                    },
                    ticks: {
                        fontSize: 10,

                    }
                }

            },
            plugins: {
                labels: false,
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    titleFontSize:20,
                    bodyFontSize:15,
                    bodySpacing:5,
                    displayColors:true,
                    callbacks:{
                        label: function(item){
                            if (item.dataset.label === t('product_detail.price'))
                                return item.dataset.label + " : " + formatCurrencyText(item.raw.y, item.raw.currency)
                                    + (item.raw.list_price && item.raw.list_price !== item.raw.y ? " / "
                                    + (item.raw.discount_rate ? "(%" + item.raw.discount_rate.toFixed(2) + ") " : "")
                                    + formatCurrencyText(item.raw.list_price, item.raw.currency) : "");
                            else
                                return item.dataset.label + " : " + item.raw.y;
                        }
                    },
                },
            }

        };
    };

    const priceDateTemplate = (rowData) => {
        return <div>
            <span className="p-column-title">{t('product_detail.date')}</span>
            {toDateTimeString(rowData.created_at)}
        </div>;
    };
    const priceValueTemplate = (rowData) => {
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product_detail.price')}</span>
            {isPriceNotListed(rowData) ? t('product_detail.no_price_listed') : formatCurrency(rowData.value, rowData.currency)}
            {rowData.is_manual && <Tag value={t('product_detail.manual_price_tag')} severity="warning" style={{marginLeft: 6, fontSize: '0.65rem'}}/>}
        </div>;
    };
    const priceSaleValueTemplate = (rowData) => {
        let sale_value = rowData && rowData.sale_value ?
            (formatCurrencyText(rowData.sale_value, rowData.currency)) :
            null;

        return rowData.sale_value !== rowData.value &&
            <div className="p-text-light p-text-nowrap" style={{fontSize:"0.9em"}}>
                <span className="strikethrough-diagonal">{sale_value}</span>
            </div>;
    };

    const priceDiscountRateTemplate = (rowData) => {
        return rowData?.discount_rate;
    }
    const priceStockTemplate  = (rowData) => {
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product_detail.stock')}</span>
            {StockTemplate(rowData.stock)}
        </div>;
    };

    const isAuthorized = (rowData) => {
        if (isCampaignManufacturer(selectedCampaign))
            if (rowData.product_url === "")
                return;
            if (selectedCampaign.competitors.some(c=> getHostName(c.url).includes(getHostName(rowData.product_url)) && (c.is_authorized || (!c.sellers || c.sellers.some(s=> s.seller_name === rowData.name && s.is_authorized)))))
                return <Tag style={{maxWidth: "10em", minWidth: "3em", textAlign: 'center'}} severity="success" value={t("campaign.competitors.authorized")} />;
            else
                return <Tag style={{maxWidth: "10em", minWidth: "3em", textAlign: 'center'}} severity="danger" value={t("campaign.competitors.not_authorized")} />;
    }

    // Chain "catalogue" campaigns feed a retailer campaign's sellers; a seller row carries
    // back a source_campaign_id/source_product_id pointing at the catalogue product it came
    // from. Resolve the campaign's name from CampaignContext for the provenance link's label.
    const campaignName = (id) => ((campaigns || []).find(c => c.id === id) || {}).name || '';

    const competitorProductUrlTemplate = (rowData) => {

        let l = document.createElement("a");
        l.href = rowData.product_url;

        return <div>
            <span className="p-column-title">{t('table_headers.name')}</span>
            <div className="p-grid">

                <div className="p-col">

            {isCampaignManufacturer(selectedCampaign) && !isSpecialCompetitor(rowData) && isViolator(rowData) &&
                <span className="pux-chip pux-chip-red" style={{marginRight:'6px'}}>{t('product_detail.tag_violation', 'Violation')}</span>}
            {hasAuthData && !isSpecialCompetitor(rowData) && isAuthorizedSeller(rowData) === false &&
                <span className="pux-chip pux-chip-violet" style={{marginRight:'6px'}}><i className="pi pi-user-minus"/> {t('product_detail.tag_unauthorized', 'Unauthorized')}</span>}
            <div onMouseEnter={(e) => {
                     setDetailData(rowData);
                     op.current.toggle(e);}}
                 onMouseLeave={(e) => op.current.toggle(e)}>
                <a target="_blank" rel="noopener noreferrer" href={rowData.product_url}
                   style={{color: ((rowData.is_mine || (isAdmin() && rowData.is_owner_store)) ? 'seagreen' : rowData.is_map ? '#c63737' : rowData.is_mrp ? '#c63737' : 'midnightblue')}}>
                    <span className="p-mb-2">
                        {rowData.is_map ? t('price_violation.map') :
                            rowData.is_mrp ? t('price_violation.mrp') :
                                rowData.name && rowData.name !== "" ? rowData.name : getHostName(rowData.product_url)}
                    </span>
                    {/* Violation campaigns demote the owner to a regular seller
                        (is_owner_store, admin-only hint): green name + home icon
                        mark the customer's own store without excluding it from
                        seller counts or violation tracking. */}
                    {isAdmin() && rowData.is_owner_store && !rowData.is_mine &&
                        <i className="pi pi-home" style={{marginLeft: '4px', color: 'seagreen'}}
                           title={t('product_detail.own_store', 'Own store')}/>}
                </a>
            </div>
            {rowData.product_name || rowData.variants && rowData.variants.length !== 0 ?
                <div  style={{fontSize:"0.8em"}}>
                    {rowData.product_name && rowData.product_name}
                    {rowData.variants && rowData.variants.length !== 0 ?
                        <Button icon="pi pi-images" className={((rowData.selected_variant && rowData.selected_variant.length !== 0) ? "p-button-success" : "p-button-danger") + " p-button-text imageButton p-justify-right"} tooltip={t('product_detail.variant_available')} onClick={() => {setSelectedCompetitor({...rowData}); setShowVariants(true);}}/>
                        : null}
                    {rowData.variants && rowData.variants.length !== 0 &&
                    rowData.selected_variant ? (rowData.selected_variant && rowData.selected_variant.length !== 0) &&
                        rowData.selected_variant.map(property => {
                            return <div>
                                <b>{property.name + " : "}</b>{property.value}
                            </div>;}) : null
                    }
                </div>
                :
                null
            }
            {rowData.source_product_id &&
                <div style={{fontSize: '0.8em'}}>
                    <a target="_blank" rel="noopener noreferrer"
                       href={"/#/productdetail?id=" + rowData.source_product_id}>
                        {t('product_detail.catalog_source', {
                            campaign: campaignName(rowData.source_campaign_id),
                            defaultValue: '{{campaign}} catalogue → product'})}
                    </a>
                </div>}
            {/*<b className="pi pi-external-link"></b>*/}
            {/*<div className="pi pi-chart-bar" title="Show History"/>
            <div className="pi pi-home" data-toggle="tooltip" title="Own Site" style={{display: (is_mine ? 'inline' : 'none') }} />*/}
                </div>
            </div>
        </div>;
    };

    const competitorLastModifiedTemplate = (rowData) => {
        const lastPrice = getLastPrice(rowData.prices);
        return <div className="p-text-left">
            <span className="p-column-title">{t('table_headers.last_check')}</span>
            {lastPrice && toDateTimeString(lastPrice.created_at)}
        </div>
    };

    // Buybox campaigns only: the seller's live buybox order (1 = winning the box).
    // Read from the last price entry's rank -- the fetch keeps it fresh even on
    // unchanged-price cycles. Null (out of the box / OOS) renders as "-".
    const competitorBuyboxRankTemplate = (rowData) => {
        const lastPrice = getLastPrice(rowData.prices);
        const rank = lastPrice && lastPrice.rank != null ? lastPrice.rank : null;
        return <div className="p-text-center">
            <span className="p-column-title">{t('product.buybox_rank')}</span>
            {rank != null
                ? <Tag severity={rank === 1 ? "success" : "info"} value={rank} style={{minWidth:'2.5em'}}/>
                : '-'}
        </div>;
    };

    const competitorDiscountRateTemplate = (rowData) => {
        const lastPrice = getLastPrice(rowData.prices);

        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.discount_rate')}</span>
            {lastPrice?.discount_rate}
        </div>;
    }

    const competitorPriceTemplate = (rowData) =>{
        const lastPrice = getLastPrice(rowData.prices);

        const lastShippingPrice = getLastPrice(rowData.shipping_prices);

        let price = isPriceNotListed(lastPrice) ? t('product_detail.no_price_listed')
            : lastPrice ? (formatCurrency(lastPrice.value, lastPrice.currency)) : "";
        let tooltip = lastPrice && lastPrice.original_value ?
            (formatCurrencyText(lastPrice.original_value, lastPrice.original_currency)) :
            null;

        let competitorPriceDetailInfo = showSetValue() && showCompetitorTags() ?
            ((lastPrice?.set_value ? (t('product.set_price') + ': ' + formatCurrencyText(lastPrice?.set_value, lastPrice.currency)) : '')
                + '\n' + (lastPrice?.tags != null ? lastPrice?.tags : ''))  :
            null;

        let sale_value = lastPrice && lastPrice.sale_value ?
            (formatCurrencyText(lastPrice.sale_value, lastPrice.currency)) :
            null;

        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product_detail.price')}</span>
            {sale_value && lastPrice.sale_value !== lastPrice.value &&
            <div className="p-text-light p-text-nowrap" style={{fontSize:"0.9em"}}>
                <span className="strikethrough-diagonal">{sale_value}</span>
            </div>}
            <div className="p-text-nowrap p-text-right">{price}
                {rowData.is_manual_price && <div><Tag value={t('product_detail.manual_price_tag')} severity="warning"
                    style={{fontSize: '0.65rem'}} title={t('product_detail.manual_price_paused_hint')}/></div>}
                {rowData.multiplier && rowData.multiplier !== 1 && <div style={{fontSize: '10px', fontWeight: 'bold', color: '#d06868'}}>( x{rowData.multiplier + " "}<i className="pi pi-bars" style={{'fontSize': '1em'}}/> )</div>}
                {isShippingPricesHandledSeparately(selectedCampaign) && lastShippingPrice && lastShippingPrice.value ? <div style={{fontSize: '10px', fontWeight: 'bold', color: '#d06868'}}>( +{lastShippingPrice.value + " "}<i className="pi pi-shopping-cart" style={{'fontSize': '1em'}}/> )</div> : ""}
                {tooltip && <Button icon="pi pi-refresh" className="imageButton" tooltip={tooltip} style={{color: 'maroon'}}/>}
                {competitorPriceDetailInfo && <Button icon="pi pi-info" className="imageButton" tooltip={competitorPriceDetailInfo} style={{color: 'blue'}}/>}
            </div>
            {lastPrice?.tags && (Array.isArray(lastPrice.tags) ? lastPrice.tags.length > 0 : lastPrice.tags) &&
            <div style={{marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'flex-end'}}>
                {(Array.isArray(lastPrice.tags) ? lastPrice.tags : [lastPrice.tags]).map((tag, i) =>
                    <span key={i} className="p-tag p-tag-rounded" title={tag}
                          style={{fontSize: '0.65rem', maxWidth: '9rem', overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  backgroundColor: '#eef2ff', color: '#3730a3'}}>{tag}</span>)}
            </div>}
        </div>;
    };

    const competitorShippingPriceTemplate = (rowData) =>{

        const lastPrice = getLastPrice(rowData.prices);
        const lastShippingPrice = getLastPrice(rowData.shipping_prices);
        let shipping_price = lastPrice && lastShippingPrice ? (formatCurrency(lastShippingPrice.value, lastPrice.currency)) : "";

        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product_detail.shipping_price')}</span>
            {shipping_price}
        </div>;
    };

    // const competitorReviewsTemplate = (rowData) =>{
    //     let rating = ReviewRating(rowData, t)
    //     return <div className="p-text-right">
    //         <span className="p-column-title">{t('product_detail.reviews')}</span>
    //         {rating}
    //     </div>;
    // };

    const competitorStockTemplate = (rowData) => {
        if (rowData.is_map || rowData.is_mrp)
            return ""

        let inactive_url_tooltip = rowData.is_url_deactivated ? rowData.is_url_deactivated : false;
        let not_available_tooltip = rowData.not_available_days && rowData.not_available_days >= 7 ? rowData.not_available_days : null;

        const lastPrice = getLastPrice(rowData.prices);

        // Nothing was observed for this row, so its stock 0 and its "no stock since" date say nothing about stock.
        if (isOwnRowCampaignInactive(selectedCampaign, rowData))
            return <div className="p-text-nowrap p-text-center">
                <span className="p-column-title">{t('product_detail.stock')}</span>
                <Button icon="pi pi-info-circle" className="imageButton" tooltip={t('product_detail.campaign_based_deactivated_seller')} style={{color: 'maroon'}}/>
            </div>;

        return <div className="p-text-nowrap p-text-center">
            <span className="p-column-title">{t('product_detail.stock')}</span>
            {!inactive_url_tooltip &&  not_available_tooltip ?<Button icon="pi pi-exclamation-triangle" className="imageButton" tooltip={t('product_detail.not_available_since', {notAvailableSince: toDateString(rowData.not_available_since)})} style={{color: 'orange'}}/> : null}
            {inactive_url_tooltip ? <Button icon="pi pi-exclamation-triangle" className="imageButton" tooltip={t('product_detail.inactive_url')} style={{color: 'maroon'}}/> : null}
            {!inactive_url_tooltip &&  !not_available_tooltip && StockTemplate(lastPrice && lastPrice.stock)}
        </div>;
    }

    // Delivery time ("termin") the site showed for the seller's offer at the last check;
    // only campaigns with settings.track_delivery_time record it.
    const competitorDeliveryTemplate = (rowData) => {
        const lastPrice = getLastPrice(rowData.prices);
        const text = lastPrice && lastPrice.stock > 0 ? formatDelivery(lastPrice.delivery, t) : null;
        return <div className="p-text-nowrap" title={lastPrice?.delivery?.text || ''}>
            <span className="p-column-title">{t('product.marketplace.delivery')}</span>
            {text || '-'}
        </div>;
    };

    const violationDaysTemplate = (rowData) => {
        if (rowData.product_url)
        {
            let tooltip = toDateTimeString(rowData.violation_since);
            return <div className="p-text-nowrap p-text-center" data-toggle="tooltip" title={tooltip}>
                <span className="p-column-title">{t('price_violation.days')}</span>
                {rowData.violation_days}
            </div>;
        }
    };

    // const competitorStatusTemplate = (rowData) => {
    //     if (rowData.is_map || rowData.is_mrp)
    //         return ""
    //     const newRequest = {
    //         campaign_id: selectedProduct.campaign_id,
    //         product_id: selectedProduct.id,
    //         url: rowData.product_url,
    //         competitor_url: rowData.product_url,
    //         type: 'price_competitor_update'
    //     };
    //
    //
    //     return <div className="p-text-right">
    //         <span className="p-column-title">{t('table_headers.scraping_status')}</span>
    //         <ScrapeStatus type="price" scraping_request={rowData.scraping_request}
    //                          parent_request={selectedProduct.scraping_request}
    //                          campaign_id={selectedProduct.campaign_id}
    //                          product_id={selectedProduct.id}
    //                          competitor_url={rowData.product_url}
    //                          newRequest={newRequest}
    //                          newRequestCompleted={props.refreshProducts}/>
    //     </div>;
    // }

    const dynamicPricingActiveTemplate = (rowData) => {
        return rowData.is_mine || rowData.is_map || rowData.is_mrp ? "" : <div className="p-text-right">
            <span className="p-column-title">{t('product_detail.repricing')}</span>
            <InputSwitch checked={rowData.is_dynamic_pricing_active} onChange={() => changeIsDynamicPricingActive(rowData)} tooltip={rowData.is_dynamic_pricing_active === true ? t('actions.exclude') : t('actions.include') }  />
        </div>;
    }

    // Activation control (InputSwitch when toggling is allowed, otherwise an info
    // button) — shared by the compact actions menu.
    const activationControl = (rowData) => {
        const allowed = rowData.is_mine
            ? !isOwnRowCampaignInactive(selectedCampaign, rowData)
            : !isCampaignInactiveSeller(selectedCampaign, rowData);
        return allowed
            ? <InputSwitch style={{marginRight:'0.3em'}} checked={rowData.is_active} onChange={() => changeIsActive(rowData)} tooltip={rowData.is_active ? t('actions.deactivate') : t('actions.activate') } />
            : <Button icon="pi pi-info-circle" className="p-button-outlined p-button-rounded p-button-danger" tooltipOptions={{position: 'bottom'}} tooltip={t('product_detail.campaign_based_deactivated_seller')} />;
    };

    // Content of the compact (kebab) actions popover for a competitor row.
    const rowActionsMenu = (rowData) => {
        if (!rowData)
            return null;
        const closeAnd = (fn) => () => { if (actionsOp.current) actionsOp.current.hide(); fn(); };
        return <div style={{display:'flex', flexDirection:'column', gap:'.5em', minWidth:'13em'}}>
            <Button icon="pi pi-pencil" className="p-button-text p-button-info" label={t('actions.edit')}
                    style={{justifyContent:'flex-start'}}
                    onClick={closeAnd(() => {setShowEditModal(true); setSelectedCompetitor({...rowData, _original_url: rowData.product_url});})}/>
            {!rowData.is_mine && isCampaignManufacturer(selectedCampaign) &&
                <Button icon="pi pi-flag" className="p-button-text p-button-help" label={t('price_violation.actions_taken')}
                        style={{justifyContent:'flex-start'}}
                        onClick={closeAnd(() => {setShowViolationActionModal(true); setSelectedCompetitor({...rowData});})}/>}
            {!rowData.is_mine &&
                <Button icon="pi pi-trash" className="p-button-text p-button-danger" label={t('actions.remove')}
                        style={{justifyContent:'flex-start'}}
                        onClick={closeAnd(() => {setShowDeleteConfirmation(true); setSelectedCompetitor({...rowData});})}/>}
        </div>;
    };

    const competitorActionTemplate = (rowData) => {
        const newRequest = {
            campaign_id: selectedProduct.campaign_id,
            product_id: selectedProduct.id,
            url: rowData.product_url,
            competitor_url: rowData.product_url,
            type: 'price_competitor_update'
        };
        if (rowData.is_map || rowData.is_mrp) {
            return ""
        }
        // Visible: activation toggle + price-check + report (ScrapeStatus).
        // Edit / violation / delete collapse into a compact kebab menu.
        return <div className="p-text-left" style={{display:'flex', alignItems:'center'}}>
            <span className="p-column-title">{t('table_headers.action')}</span>
            {activationControl(rowData)}
            {/* Buybox (Marketplace API) campaigns don't scrape per competitor --
                prices come from the marketplace API -- so the per-row price-check
                / scrape-status control is meaningless there and is hidden. */}
            {!isBuyboxCampaign &&
            <ScrapeStatus type="price" scraping_request={rowData.scraping_request}
                          parent_request={selectedProduct.scraping_request}
                          campaign_id={selectedProduct.campaign_id}
                          product_id={selectedProduct.id}
                          competitor_url={rowData.product_url}
                          newRequest={newRequest}
                          newRequestCompleted={props.refreshProducts}/>}
            <Button icon="pi pi-ellipsis-v" className="p-button-text p-button-rounded p-button-secondary"
                    tooltipOptions={{position: 'bottom'}} tooltip={t('table_headers.action')}
                    onClick={(e) => {setSelectedCompetitor({...rowData}); actionsOp.current.toggle(e);}}/>
        </div>;
    }

    const groupHeaderTemplate = (rowData) =>{

        return <div style={{backgroundColor:rowData.groupColor}}>{rowData.groupName}</div>;
    };
    const groupFooterTemplate = (rowData) =>{
        return null;
    };

    const toggleMarketplaceGroup = (key) => setMpExpandedKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Group header of the marketplace operator profile: marketplace name, its
    // cheapest in-stock price and seller, its seller count and the expand/collapse
    // toggle, led by its 1-based price position (blank for unpriced groups). The own
    // group's header is highlighted. A collapsed group shows nothing but this header.
    // The own stores group (the operator's shops on other marketplaces) has no position
    // and says it is not counted as a competitor.
    const marketplaceGroupHeaderTemplate = (rowData) => {
        const key = rowData.mpGroupKey;
        const isOwnStores = key === OWN_STORES_KEY;
        const isOwn = !isOwnStores && !!ownMarketplaceKey && key === ownMarketplaceKey;
        const collapsed = !mpExpandedKeys.has(key);
        const label = isOwnStores ? t('product_detail.marketplace.own_stores_group', operatorTextOptions(selectedCampaign?.settings))
            : !key ? t('product_detail.marketplace.other')
            : isOwn ? `${ownAwareMarketplaceLabel(key, selectedCampaign?.settings)} (${t('product_detail.marketplace.own_marketplace')})`
            : marketplaceLabel(key);
        return <div className={`mp-group-header preventSelect${isOwn ? ' mp-own-group' : ''}${isOwnStores ? ' mp-own-stores-group' : ''}`}>
            <span className="mp-group-position">{rowData.mpGroupPosition != null ? `${rowData.mpGroupPosition}.` : ''}</span>
            <Button type="button" className="p-button-text p-button-rounded p-button-sm p-button-secondary preventSelect"
                    icon={collapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-down'}
                    tooltip={t(collapsed ? 'product_detail.marketplace.expand' : 'product_detail.marketplace.collapse')}
                    tooltipOptions={{position: 'bottom'}}
                    onClick={(e) => { e.stopPropagation(); toggleMarketplaceGroup(key); }}/>
            <b>{label}</b>
            {isOwnStores && <span className="mp-group-header-muted mp-group-header-hint">{t('product_detail.marketplace.own_stores_hint')}</span>}
            <span>{rowData.mpGroupMin != null ? formatCurrency(rowData.mpGroupMin, rowData.mpGroupCurrency) : '-'}</span>
            {rowData.mpGroupMin != null && rowData.mpGroupMinSeller &&
                <span className="mp-group-header-muted">{rowData.mpGroupMinSeller}</span>}
            {rowData.mpGroupMin != null && formatDelivery(rowData.mpGroupMinDelivery, t) &&
                <span className="mp-group-header-muted" title={t('product.marketplace.delivery')}>
                    <i className="pi pi-truck" style={{fontSize: '.72rem', marginRight: 4}}/>{formatDelivery(rowData.mpGroupMinDelivery, t, rowData.mpGroupDeliveryIsBuybox)}
                </span>}
            <span className="mp-group-header-muted">{t('product_detail.marketplace.n_sellers', {count: rowData.mpGroupCount})}</span>
        </div>;
    };

    // The hidden placeholder row of a collapsed marketplace group only exists so the
    // group header renders; no cell template may act on it.
    const skipPlaceholder = (template) => (rowData, options) => (rowData?.mpPlaceholder ? null : template(rowData, options));

    // Row number that does not count placeholder rows.
    const sellerIndexTemplate = (rowData, options) => {
        if (rowData?.mpPlaceholder) return null;
        const before = competitors.slice(0, options.rowIndex).filter(c => !c.mpPlaceholder).length;
        return IndexTemplate(rowData, {...options, rowIndex: before});
    };

    const normalizeUrl = (url) => {
        return url ? url.trim().replace(/\/$/, "").toLowerCase() : "";
    };

    const isHighlightedCompetitor = (rowData) => {
        const highlightedUrl = normalizeUrl(props.highlightCompetitorUrl);
        return highlightedUrl !== "" &&
            (normalizeUrl(rowData.product_url) === highlightedUrl || normalizeUrl(rowData.parent_url) === highlightedUrl);
    };

    // ----- seller filter chips -------------------------------------------------
    const isSpecialCompetitor = (c) => c.is_mine || c.is_map || c.is_mrp;

    // Mirrors the visible Stock column (StockTemplate): in stock iff the latest
    // price reports stock >= 1.
    const isInStock = (c) => { const lp = getLastPrice(c.prices); return !!lp && lp.stock >= 1; };
    const isLongOutOfStock = (c) => !!c.not_available_days && c.not_available_days >= 7;

    // Brand: MAP/MRP come from the is_map/is_mrp reference rows (same source as the table).
    const mapRefPrice = (() => { const r = selectedProduct.competitors.find(c => c.is_map); const lp = r && getLastPrice(r.prices); return lp && lp.value != null ? lp.value : 0; })();
    const mrpRefPrice = (() => { const r = selectedProduct.competitors.find(c => c.is_mrp); const lp = r && getLastPrice(r.prices); return lp && lp.value != null ? lp.value : 0; })();
    const isMapViolator = (c) => { const lp = getLastPrice(c.prices); return !!(lp && lp.value != null && mapRefPrice > 0 && lp.value < mapRefPrice); };
    const isMrpViolator = (c) => { const lp = getLastPrice(c.prices); return !!(lp && lp.value != null && mrpRefPrice > 0 && lp.value > mrpRefPrice); };
    const isViolator = (c) => isMapViolator(c) || isMrpViolator(c);
    // is_authorized is a whitelist flag (default false). A seller is authorized only when
    // its site or its matching seller is explicitly is_authorized === true; otherwise it is
    // unauthorized. The unauthorized filter/tag is shown only when the campaign actually has
    // authorization configured (hasAuthData), so we don't flag everything by default.
    const isAuthorizedSeller = (c) => {
        if (!c.product_url) return null;
        const host = getHostName(c.product_url);
        const site = (selectedCampaign.competitors || []).find(comp => getHostName(comp.url).includes(host));
        if (!site) return false;
        if (site.is_authorized) return true;
        return !!(site.sellers || []).some(s => s.seller_name === c.name && s.is_authorized);
    };
    // Real sellers (non-reference, non-parent) — base set for the brand chip counts.
    const brandBase = selectedProduct.competitors.filter(c => !isSpecialCompetitor(c) && c.is_parent_url !== true);
    // Authorization is configured only when some site/seller is explicitly authorized.
    const hasAuthData = isCampaignManufacturer(selectedCampaign) && !!selectedCampaign.competitors?.some(c => c.is_authorized || c.sellers?.some(s => s.is_authorized));

    const sellerFilterPredicates = {
        // Stock buckets are mutually exclusive and exclude URL-invalid / long-out
        // records (those have their own chips), matching the Stock column order:
        // URL invalid  >  long out of stock  >  in stock / out of stock.
        in_stock: (c) => !c.is_url_deactivated && !isLongOutOfStock(c) && isInStock(c),
        out_of_stock: (c) => !c.is_url_deactivated && !isLongOutOfStock(c) && !isInStock(c),
        long_out_of_stock: (c) => !c.is_url_deactivated && isLongOutOfStock(c),
        url_invalid: (c) => !!c.is_url_deactivated,
        inactive: (c) => c.is_active === false,
        campaign_inactive: (c) => isCampaignInactiveSeller(selectedCampaign, c),
        parent: (c) => c.is_parent_url === true,
    };

    const sellerFilterChips = [
        {key: 'all', label: t('product_detail.filter_all')},
        {key: 'in_stock', label: t('product_detail.filter_in_stock')},
        {key: 'out_of_stock', label: t('product_detail.filter_out_of_stock')},
        {key: 'long_out_of_stock', label: t('product_detail.filter_long_out_of_stock')},
        {key: 'url_invalid', label: t('product_detail.filter_url_invalid')},
        {key: 'inactive', label: t('product_detail.filter_inactive')},
        {key: 'campaign_inactive', label: t('product_detail.filter_campaign_inactive')},
        {key: 'parent', label: t('product_detail.filter_parent')},
    ];

    const sellerFilterBaseList = () =>
        selectedProduct.competitors.filter(c => (c.is_parent_url !== true || isAdmin()) && !isSpecialCompetitor(c));

    const sellerFilterCount = (key) => {
        const base = sellerFilterBaseList();
        if (key === 'all')
            return base.length;
        if (key === 'parent')
            return base.filter(c => c.is_parent_url === true).length;
        // Parent URLs only belong to the 'parent' chip, not the stock/status chips.
        const pred = sellerFilterPredicates[key];
        return pred ? base.filter(c => !c.is_parent_url && pred(c)).length : 0;
    };

    // Default selection is in/out of stock; when both buckets are empty but
    // long-out-of-stock sellers exist, include them so the default view is not
    // an empty table.
    useEffect(() => {
        if (sellerFiltersTouched.current) return;
        const next = ['in_stock', 'out_of_stock'];
        if (sellerFilterCount('in_stock') === 0 && sellerFilterCount('out_of_stock') === 0 && sellerFilterCount('long_out_of_stock') > 0)
            next.push('long_out_of_stock');
        setSellerFilters(prev => prev.join() === next.join() ? prev : next);
    }, [props.selectedProduct]);

    const toggleSellerFilter = (key) => {
        sellerFiltersTouched.current = true;
        if (key === 'all') {
            setSellerFilters(['all']);
            return;
        }
        setSellerFilters(prev => {
            let next = prev.filter(x => x !== 'all');
            next = next.includes(key) ? next.filter(x => x !== key) : [...next, key];
            return next.length === 0 ? ['all'] : next;
        });
    };

    const matchesSellerFilter = (c) => {
        if (isSpecialCompetitor(c))
            return true;
        // Brand violation + authorization dimensions AND with the stock filter (real sellers only).
        if (!c.is_parent_url) {
            if (violationFilter === 'map' && !isMapViolator(c)) return false;
            if (violationFilter === 'mrp' && !isMrpViolator(c)) return false;
            if (violationFilter === 'none' && isViolator(c)) return false;
            if (unauthorizedOnly && isAuthorizedSeller(c) !== false) return false;
        }
        if (sellerFilters.includes('all'))
            return true;
        // Parent URLs match ONLY the 'parent' chip; they never leak into the
        // stock/status chips.
        if (c.is_parent_url)
            return sellerFilters.includes('parent');
        return sellerFilters.some(k => k !== 'parent' && sellerFilterPredicates[k] && sellerFilterPredicates[k](c));
    };

    const getCompetitors = () =>{

        let competitors = selectedProduct.competitors.filter(c=> c.is_parent_url !== true || isAdmin());

        if (isCampaignManufacturer(selectedCampaign)){
            const mapPrice = selectedProduct.competitors.find(c=>c.is_map) && getLastPrice(selectedProduct.competitors.find(c=>c.is_map).prices) ? getLastPrice(selectedProduct.competitors.find(c=>c.is_map).prices).value : 0;
            const mrpPrice = selectedProduct.competitors.find(c=>c.is_mrp) && getLastPrice(selectedProduct.competitors.find(c=>c.is_mrp).prices) ? getLastPrice(selectedProduct.competitors.find(c=>c.is_mrp).prices).value : 0;

            // competitors.forEach(c =>{
            //     const lastPrice = getLastPrice(c.prices);
            //     if (!selectedCampaign.settings?.stock_including_for_map && lastPrice &&  !lastPrice.stock)
            //     {
            //         c.groupName = "NotAvailable";
            //         c.groupColor = "";
            //     }
            //     else if(lastPrice && lastPrice.value < mapPrice)
            //     {
            //         c.groupName = "ViolatesMap";
            //         c.groupColor = "drop_shipping";
            //     }
            //     else if(lastPrice && mrpPrice > 0 && lastPrice.value > mrpPrice)
            //     {
            //         c.groupName = "ViolatesMrp";
            //         c.groupColor = "drop_shipping";
            //     }
            //     else if(!lastPrice){
            //         c.groupName = "NotAvailable";
            //         c.groupColor = "";
            //     }
            //     else
            //     {
            //         c.groupName = "OK";
            //         c.groupColor = "former";
            //     }
            // });
        }
        else{
            const myPrice = selectedProduct.competitors.find(c=>c.is_mine) && getLastPrice(selectedProduct.competitors.find(c=>c.is_mine).prices) ? getLastPrice(selectedProduct.competitors.find(c=>c.is_mine).prices).value : 0;

            // competitors.forEach(c =>{
            //     const lastPrice = getLastPrice(c.prices);
            //     if (lastPrice && !lastPrice.stock)
            //     {
            //         c.groupName = "NotAvailable";
            //         c.groupColor = "";
            //     }
            //     else if(lastPrice && lastPrice.value < myPrice)
            //     {
            //         c.groupName = "Cheap";
            //         c.groupColor = "var(--pink-300)";
            //     }
            //     else if(lastPrice && lastPrice.value === myPrice)
            //     {
            //         c.groupName = "Same";
            //         c.groupColor = "";
            //     }
            //     else if(lastPrice && lastPrice.value > myPrice)
            //     {
            //         c.groupName = "Expensive";
            //         c.groupColor = "var(--green-300)";
            //     }
            //     else
            //     {
            //         c.groupName = "OK";
            //         c.groupColor = "var(--pink-300)";
            //     }
            // });
        }


        let result = props.highlightCompetitorUrl
            ? competitors.filter(isHighlightedCompetitor)
            : competitors.filter(matchesSellerFilter);

        // Buybox campaigns: order sellers by their live buybox rank (winner first);
        // sellers without a rank (out of the box / OOS) sink to the bottom.
        if (isBuyboxCampaign) {
            const rankOf = (c) => {
                const lp = getLastPrice(c.prices);
                return lp && lp.rank != null ? lp.rank : Number.POSITIVE_INFINITY;
            };
            result = [...result].sort((a, b) => rankOf(a) - rankOf(b));
        }

        return result;
    };

    let competitors = getCompetitors();
    if (isMarketplaceProfile) {
        const groupKeys = groupSellerRows(competitors, marketplaceConfig, new Set()).map(c => c.mpGroupKey);
        const collapsedKeys = new Set(groupKeys.filter(key => !mpExpandedKeys.has(key)));
        competitors = groupSellerRows(competitors, marketplaceConfig, collapsedKeys);
    }

    const variantsTemplate = (rowData) => {
        let variantList = null;
        if (rowData.properties !== undefined && rowData.properties !== null) {
            variantList = rowData.properties.map(property => {
                return <React.Fragment><div><b>{property.name + " : "}</b>{property.value}</div></React.Fragment>;
            });
        }

        return <div className="">
            <span className="p-column-title">{t('product_detail.variants')}</span>
            {variantList}
        </div>;
    };
    const variantPriceTemplate = (rowData) =><div className="p-text-right">
        <span className="p-column-title">{t('product_detail.price')}</span>
        {formatCurrency(rowData.price, rowData.currency)}
    </div>;
    const variantStockTemplate = (rowData) => <div className="p-text-right">
        <span className="p-column-title">{t('product_detail.stock')}</span>
        {rowData.stock ? <i style={{color:"lightgreen"}} className="pi pi-check-circle" /> : <i style={{color:"red"}} className="pi pi-times-circle" />}
    </div>;

    const selectedVariantRow = () =>{
        return selectedCompetitor.variants.find(variant => JSON.stringify(variant.properties) === JSON.stringify(selectedCompetitor.selected_variant));
    };

    const onChange = (e) =>{
        const {name, value} = e.target;
        let error = urlValidator(value, t);
        setProductUrl(value);
        setFormErrors({[name]: error});
    };

    const onEnter = (e) => e.key === "Enter" && addCompetitor();

    const showDiscountType = () => {
        return selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "competitors.discount_type" && sf.selected) ;
    } ;

    const showDiscountRate = () => {
        return selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "competitors.prices.discount_rate" && sf.selected) ;
    } ;

    const showSetValue = () => {
        return selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "competitors.prices.set_value" && sf.selected) ;
    } ;

    const showCompetitorTags = () => {
        return selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "competitors.prices.tags" && sf.selected) ;
    } ;

    const showShippingPrices = () => {
        return !isShippingPricesHandledSeparately(selectedCampaign) && selectedProduct.competitors.some(c=>
            c.shipping_prices && c.shipping_prices.length > 0
        );
    } ;
    // const showReviews = () => {
    //     return selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "competitors.prices.rating" && sf.selected) && selectedProduct.competitors.some(c=>
    //         c.prices && c.prices.length > 0 && c.prices[c.prices.length - 1].rating
    //     );
    // } ;

    const decisionColor = (rowData) => {
        let rowClassNames = {};
        // Highlight the owner ("You") row in buybox campaigns so it stands out
        // among the marketplace sellers.
        if (isBuyboxCampaign && rowData.is_mine)
            rowClassNames.owner_row = true;
        if (isHighlightedCompetitor(rowData))
            rowClassNames.competitor_url_highlight = true;

        if (rowData.groupColor)
        {
            rowClassNames[rowData.groupColor] = true;
            return rowClassNames;
        }
        else if (rowData.is_drop_shipping)
            rowClassNames.drop_shipping = true;
        else if (!rowData.is_active)
            rowClassNames.deactivated = true;
        else if (rowData.is_parent_url && isAdmin())
            rowClassNames.parent_url = true;
        return Object.keys(rowClassNames).length > 0 ? rowClassNames : "";
    };

    // The 1P row of a marketplace operator product stands out among the sellers.
    const marketplaceRowClassName = (rowData) => {
        if (rowData.mpPlaceholder) return {'mp-group-placeholder': true};
        const base = decisionColor(rowData);
        // Own store rows are inactive on purpose (never competitors), so they are not
        // styled as deactivated: their price is the point of the group.
        if (rowData.is_own_store) {
            const classes = {...(base || {}), 'mp-own-store-row': true};
            delete classes.deactivated;
            return classes;
        }
        if (!rowData.is_first_party) return base;
        return {...(base || {}), 'mp-first-party': true};
    };

    const onCompetitorChange = (e) => {
        const { key, value } = getKeyValue(e);
        setSelectedCompetitor({...selectedCompetitor, [key] : value});
    };

    const onNewPriceChange = (e) => {
        const { key, value } = getKeyValue(e);
        setNewPrice({...newPrice, [key] : value});
    }

    const deleteFamily = getDeleteFamily(selectedCompetitor);
    const deleteListing = deleteFamily.find(p => p.is_parent_url === true);
    // The listing is the product's own page: it can never be deleted, so every
    // seller hanging off it is recreated on the next price check. Deactivating
    // the seller is then the only action that sticks.
    const ownListing = deleteFamily.find(p => p.is_mine);
    const canDeactivateSeller = selectedCompetitor && !selectedCompetitor.is_mine && selectedCompetitor.is_active !== false;
    // Only a family with more than the clicked row leaves the user a choice.
    const hasSellerChoice = !ownListing && deleteFamily.length > 1;
    const selectedSellerName = selectedCompetitor
        ? (selectedCompetitor.name || getHostName(selectedCompetitor.product_url)) : '';
    // The listing row is not one of its sellers, so it is not counted as one.
    const deleteSellerCount = deleteFamily.length - (deleteListing ? 1 : 0);

    const modalYesNo = (
        selectedCompetitor && ownListing ?
            (canDeactivateSeller ?
                <div>
                    <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-text" onClick={closeDeleteConfirmation} />
                    <Button label={t('actions.deactivate')} icon="pi pi-ban" className="p-button-warning" onClick={deactivateCompetitorOnly} />
                </div>
                :
                <Button label={t('yes_no.ok')} icon="pi pi-check" className="p-button-info" onClick={closeDeleteConfirmation} />)
        :
        <div>
            <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-warning" onClick={closeDeleteConfirmation} />
            <Button label={t('yes_no.yes')} icon="pi pi-check" className="p-button-danger"
                    onClick={hasSellerChoice && deleteMode === 'seller' ? deactivateCompetitorOnly : deleteCompetitor} />
        </div>
    );

    return(
        <React.Fragment>
            <Sidebar showCloseIcon={false} visible={dialogVisible} style={{width:'26em', overflow: "auto"}} position="right" onHide={()=>{setDialogVisible(false);}}>
                {/* Header: icon badge + title */}
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6}}>
                    <span style={{width: 40, height: 40, borderRadius: 10, background: '#ecfdf5', color: '#059669',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        <i className="pi pi-plus" style={{fontSize: '1.1rem'}}/>
                    </span>
                    <div style={{fontWeight: 700, fontSize: '1.15rem'}}>{t('product_detail.add_competitor')}</div>
                </div>
                <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 20px'}}>{t('product_detail.add_competitor_hint')}</p>

                {/* Seller URL field with leading link icon */}
                <div>
                    <label htmlFor="product_url" style={{display: 'block', fontWeight: 600, fontSize: '.85rem',
                                                          color: '#374151', marginBottom: 6}}>
                        {t('product_detail.competitor_url')}
                    </label>
                    <span className="p-input-icon-left" style={{display: 'block'}}>
                        <i className="pi pi-link"/>
                        <InputText id="product_url" name='product_url' value={product_url} onChange={onChange} onKeyDown={onEnter}
                                   placeholder="https://..."
                                   className={formErrors.product_url.length > 0 ? "p-invalid" : ""}
                                   style={{width: '100%'}} autoFocus={true}/>
                    </span>
                    {formErrors.product_url.length > 0 &&
                        (<Message severity="error" text={formErrors.product_url}
                                  style={{marginTop: 10, width: '100%', justifyContent: 'flex-start'}}/>)}
                </div>

                {canEnterManualPrices() &&
                <div style={{marginTop: 18}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <InputSwitch inputId="manual_seller" checked={manualSeller.enabled}
                                     onChange={(e) => setManualSeller({...manualSeller, enabled: e.value})}/>
                        <label htmlFor="manual_seller" style={{fontWeight: 600, fontSize: '.85rem'}}>{t('product_detail.manual_seller')}</label>
                    </div>
                    <small style={{color: '#6b7280', display: 'block', marginTop: 6}}>{t('product_detail.manual_seller_hint')}</small>
                    {manualSeller.enabled &&
                    <div style={{display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12}}>
                        <InputText value={manualSeller.product_name} placeholder={t('product_detail.manual_seller_product_name')}
                                   onChange={(e) => setManualSeller({...manualSeller, product_name: e.target.value})}/>
                        <div style={{display: 'flex', gap: 8}}>
                            <InputNumber value={manualSeller.price} placeholder={t('product_detail.price')} mode="decimal" minFractionDigits={2}
                                         onValueChange={(e) => setManualSeller({...manualSeller, price: e.value})} style={{flex: 1}}/>
                            <InputText value={manualSeller.currency || ownCurrency()} style={{width: 70}}
                                       onChange={(e) => setManualSeller({...manualSeller, currency: e.target.value.toUpperCase()})}/>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <InputSwitch inputId="manual_seller_stock" checked={manualSeller.in_stock}
                                         onChange={(e) => setManualSeller({...manualSeller, in_stock: e.value})}/>
                            <label htmlFor="manual_seller_stock">{t('product.stock')}</label>
                        </div>
                    </div>}
                </div>}

                {/* Footer actions */}
                <div style={{display: 'flex', gap: 10, marginTop: 24}}>
                    <Button id="cancel" label={t('actions.cancel')} icon="pi pi-angle-double-left"
                            className="p-button-text p-button-secondary" style={{flex: 1}} onClick={()=>{setDialogVisible(false)}}/>
                    <Button id="add" label={t('actions.add')} icon="pi pi-plus"
                            className="p-button-success" style={{flex: 1}} onClick={addCompetitor}/>
                </div>
            </Sidebar>

            {selectedCompetitor && showEditModal &&
                <Dialog visible={showEditModal} header={t('product_detail.edit_competitor') + ' - ' + selectedCompetitor.name} style={{maxWidth: '60vw', minWidth: '30vw'}} modal={true} onHide={() => {setShowEditModal(false); setSelectedCompetitor(null);}}>
                    <div className="p-grid p-fluid alignCenter">
                        {!selectedCompetitor.is_mine &&
                        <div className="p-col-12">
                            <label htmlFor="product_url" style={{display:'block', marginBottom:'.35em'}}>{t('product_detail.competitor_url')}</label>
                            <InputText id="product_url" name="product_url" value={selectedCompetitor.product_url || ''} style={{width:"100%"}}
                                       onChange={onCompetitorChange}/>
                        </div>}
                        <div className="p-col-12">
                            <span className="p-float-label">
                                {/* This URL's own multiplier, never the effective one: an
                                    inherited seller/site value is named in the hint below so
                                    saving the dialog cannot freeze it into a URL override. */}
                                <InputNumber id="url_multiplier" name="url_multiplier" value={selectedCompetitor.url_multiplier} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={1}
                                             onValueChange={onCompetitorChange}/>
                                <label htmlFor="url_multiplier">{t('product_detail.multiplier')}</label>
                            </span>
                            {inheritedMultiplier(selectedCompetitor) &&
                                <small style={{color: '#6b7280'}}>
                                    {t('product_detail.inherited_multiplier', 'Satıcı / site çarpanı: {{value}}',
                                       {value: inheritedMultiplier(selectedCompetitor)})}
                                </small>}
                        </div>
                        {showDiscountType() &&
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputText id="discount_type" name="discount_type" value={selectedCompetitor.discount_type} style={{width:"200px"}}
                                             onChange={onCompetitorChange}/>
                                <label htmlFor="discount_type">{t('product.discount_type')}</label>
                            </span>
                        </div>}
                        {showShippingPrices() &&
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="manual_shipping_price" name="manual_shipping_price" value={selectedCompetitor.manual_shipping_price} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={1}
                                             onValueChange={onCompetitorChange}/>
                                <label htmlFor="manual_shipping_price">{t('product_detail.shipping_price')}</label>
                            </span>
                        </div>}
                        {showShippingPrices() &&
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="manual_shipping_price_threshold" name="manual_shipping_price_threshold" value={selectedCompetitor.manual_shipping_price_threshold} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={1}
                                             onValueChange={onCompetitorChange}/>
                                <label htmlFor="manual_shipping_price_threshold">{t('product_detail.shipping_price_threshold')}</label>
                            </span>
                        </div>}
                        <div className="p-col-6">
                            <Button id="cancel" label={t('actions.cancel')} icon="pi pi-angle-double-left" className="p-button-outlined p-button-warning" onClick={()=>{setShowEditModal(false);}}/>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.save')} icon="pi pi-save" className="p-button-outlined p-button-success" onClick={updateCompetitor}/>
                        </div>
                    </div>
                </Dialog>
            }

            {newPrice && showAddPriceModal &&
                <Dialog visible={true} header={t('product_detail.edit_competitor') + ' - ' + selectedCompetitor.name} style={{maxWidth: '60vw', minWidth: '30vw'}} modal={true} onHide={() => {setShowAddPriceModal(false); setNewPrice(null);}}>
                    <div className="p-grid p-fluid alignCenter">
                        <div className="p-col-12">
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_price" name="added_price" value={newPrice.added_price} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={1}
                                             onValueChange={onNewPriceChange}/>
                                <label htmlFor="price">{t('product_detail.price')}</label>
                            </span>
                        </div>

                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_sale_price" name="added_sale_price" value={newPrice.added_sale_price} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={1}
                                             onValueChange={onNewPriceChange}/>
                                <label htmlFor="sale_price">{t('product.list_price')}</label>
                            </span>
                        </div>

                        <div className="p-col-12">
                            <span className="p-float-label">
                                <span className="p-float-label">
                                    <Dropdown id="added_currency" name="added_currency" value={newPrice.added_currency} style={{width:"200px"}} options={currencies} filter showClear onChange={onNewPriceChange}/>
                                    <label htmlFor="currency">{t('campaign.currency')}</label>
                                </span>
                            </span>
                        </div>

                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_stock" name="added_stock" value={newPrice.added_stock ? newPrice.added_stock : 0} style={{width:"200px"}}
                                             mode="decimal" minFractionDigits={0}
                                             onValueChange={onNewPriceChange}/>
                                <label htmlFor="stock">{t('product.stock')}</label>
                            </span>
                        </div>

                        <div className="p-col-12">
                            <span className="p-float-label">
                                <span className="p-float-label">
                                    <Calendar id="added_date" name="added_date" readOnlyInput={true} value={newPrice.added_date} onChange={onNewPriceChange} showIcon={true} dateFormat="yy-mm-dd" style={{marginRight:'0.5em', width:"200px"}} />
                                    <label htmlFor="added_date">{t('product_detail.date')}</label>
                                </span>
                            </span>
                        </div>

                        {!selectedCompetitor.is_mine &&
                        <div className="p-col-12" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <InputSwitch inputId="pause_automatic_check" checked={!!newPrice.pause_automatic_check}
                                         onChange={(e) => setNewPrice({...newPrice, pause_automatic_check: e.value})}/>
                            <label htmlFor="pause_automatic_check">{t('product_detail.pause_automatic_check')}</label>
                        </div>}

                        <div className="p-col-6">
                            <Button id="cancel" label={t('actions.cancel')} icon="pi pi-angle-double-left" className="p-button-outlined p-button-warning" onClick={() => {setShowAddPriceModal(false); setNewPrice(null);}}/>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.save')} icon="pi pi-save" className="p-button-outlined p-button-success" onClick={addCompetitorPrice}/>
                        </div>
                    </div>
                </Dialog>
            }

            {selectedCompetitor && showViolationActionModal &&
            <Dialog visible={showViolationActionModal} header={t('price_violation.actions_taken') + ' - ' + selectedCompetitor.name} style={{maxWidth: '60vw', minWidth: '30vw'}} modal={true} onHide={() => {setShowViolationActionModal(false); setSelectedCompetitor(null);}}>
                <CompetitorViolationAction selectedProduct={selectedProduct} selectedCompetitor={selectedCompetitor} />
            </Dialog>
            }

            <Dialog visible={showVariants} header={t('product_detail.select_variants')} style={{maxWidth: '60vw', minWidth: '20vw'}} modal={true} onHide={()=>{setShowVariants(false);}}>
                {selectedCompetitor && showVariants ?

                    <DataTable
                        value={selectedCompetitor.variants}
                        responsive={true}
                        selectionMode="single"
                        selection={selectedVariantRow()}
                        onSelectionChange={matchVariant}
                        resizableColumns={true} columnResizeMode="fit">
                        <Column body={IndexTemplate} headerStyle={{width:'3em'}} />
                        <Column header={t('product_detail.variants')} body={variantsTemplate} headerStyle={{width:'60%'}}/>
                        <Column header={t('product_detail.price')} field="price" body={variantPriceTemplate} headerStyle={{textAlign:"right", width:'20%'}}/>
                        <Column header={t('product_detail.stock')} field="stock" body={variantStockTemplate} headerStyle={{textAlign:"right", width:'20%'}}/>
                    </DataTable>:""
                }
            </Dialog>


            <OverlayPanel ref={op} dismissable={true}>
                {detailData &&
                <span>
                    {/*{detailData.product_name}*/}
                    {/*{(detailData.selected_variant && detailData.selected_variant.length !== 0) &&*/}
                    {/*detailData.selected_variant.map(property => {*/}
                    {/*    return <div>*/}
                    {/*        <b>{property.name + " : "}</b>{property.value}*/}
                    {/*    </div>;*/}
                    {/*})*/}
                    {/*}*/}
                    <img referrerPolicy='no-referrer' className="img-fluid-thumb" src={detailData.product_image} alt=""/>
                    {detailData.prices !== null && detailData.prices.length > 0 && <img referrerPolicy='no-referrer' className="img-fluid-thumb" src={detailData.prices[detailData.prices.length - 1]?.promotion_image_url} alt=""/>}
                </span> }
            </OverlayPanel>

            <OverlayPanel ref={actionsOp} dismissable={true}>
                {rowActionsMenu(selectedCompetitor)}
            </OverlayPanel>

            <div className="pux-fchips">
                {sellerFilterChips.map(chip => {
                    const count = sellerFilterCount(chip.key);
                    if (chip.key === 'parent' && (count === 0 || !isAdmin()))
                        return null;
                    const selected = sellerFilters.includes(chip.key);
                    return <button type="button" key={chip.key}
                                   className={`pux-chip pux-chip-toggle ${selected ? 'pux-chip-blue' : 'off'}`}
                                   onClick={() => toggleSellerFilter(chip.key)}>{`${chip.label} (${count})`}</button>;
                })}
                {isCampaignManufacturer(selectedCampaign) && <>
                    <button type="button" className={`pux-chip pux-chip-toggle ${violationFilter === 'map' ? 'pux-chip-red' : 'off'}`}
                            onClick={() => setViolationFilter(v => v === 'map' ? null : 'map')}>{`${t('product.filter_violation_map_label')} (${brandBase.filter(isMapViolator).length})`}</button>
                    <button type="button" className={`pux-chip pux-chip-toggle ${violationFilter === 'mrp' ? 'pux-chip-red' : 'off'}`}
                            onClick={() => setViolationFilter(v => v === 'mrp' ? null : 'mrp')}>{`${t('product.filter_violation_mrp_label')} (${brandBase.filter(isMrpViolator).length})`}</button>
                    <button type="button" className={`pux-chip pux-chip-toggle ${violationFilter === 'none' ? 'pux-chip-green' : 'off'}`}
                            onClick={() => setViolationFilter(v => v === 'none' ? null : 'none')}>{`${t('product.filter_violation_none')} (${brandBase.filter(c => !isViolator(c)).length})`}</button>
                    {hasAuthData &&
                    <button type="button" className={`pux-chip pux-chip-toggle ${unauthorizedOnly ? 'pux-chip-violet' : 'off'}`}
                            onClick={() => setUnauthorizedOnly(v => v ? null : true)}><i className="pi pi-user-minus"/> {`${t('product.filter_unauthorized')} (${brandBase.filter(c => isAuthorizedSeller(c) === false).length})`}</button>}
                </>}
                <span style={{marginLeft:'auto', display:'flex', gap:'.4em'}}>
                    <Button tooltip={t('actions.bulk_operations')} tooltipOptions={{position:'bottom'}} icon="pi pi-sort-alt"
                            className="p-button-outlined p-button-warning p-button-sm" onClick={() => setShowBulkOperations(true)}/>
                    {!isBuyboxCampaign &&
                    <Button label={t('product_detail.add_competitor')} icon="pi pi-plus"
                            className="p-button-outlined p-button-success p-button-sm" onClick={addDialog}/>}
                </span>
            </div>

            <DataTable value={competitors}
                       rowGroupMode={isMarketplaceProfile ? "subheader" : undefined}
                       groupField={isMarketplaceProfile ? "mpGroup" : undefined}
                       rowGroupHeaderTemplate={isMarketplaceProfile ? marketplaceGroupHeaderTemplate : undefined}
                       // PrimeReact 6 calls the footer template for every subheader group.
                       rowGroupFooterTemplate={isMarketplaceProfile ? groupFooterTemplate : undefined}
                       //rowGroupMode="subheader"
                       //groupField="groupName"
                       //rowGroupHeaderTemplate={groupHeaderTemplate}
                       //rowGroupFooterTemplate={groupFooterTemplate}
                       // expandableRowGroups={true}
                       responsive={true}
                       expandedRows={expandedCompetitor}
                       onRowToggle={(e) => {
                           setExpandedCompetitor(Array.isArray(e.data) ? e.data.filter(c => !c?.mpPlaceholder) : e.data);
                       }}
                       onSelectionChange={(e) => {
                           if(!isPreventSelect(e.originalEvent.target))
                               setExpandedCompetitor(Array.isArray(e.value) ? e.value.filter(c => !c?.mpPlaceholder) : e.value)
                       }}
                       selection={expandedCompetitor}
                       selectionMode="multiple"
                       metaKeySelection={false}
                       rowClassName={isMarketplaceProfile ? marketplaceRowClassName : decisionColor}
                       rowExpansionTemplate={competitorRowExpansionTemplate}
                       resizableColumns={true} columnResizeMode="fit">
                <Column expander={true} style={{width:"3.3em"}}/>
                {!isBuyboxCampaign && <Column body={isMarketplaceProfile ? sellerIndexTemplate : IndexTemplate} headerStyle={{width:'3em'}} />}
                {isBuyboxCampaign && <Column header={t('product.buybox_rank')} headerStyle={{textAlign:"center", width:'8em'}} style={{textAlign:"center"}} body={skipPlaceholder(competitorBuyboxRankTemplate)}/>}
                <Column header={t('table_headers.name')} field="competitorUrl" body={skipPlaceholder(competitorProductUrlTemplate)} headerStyle={{width:'40%'}}/>
                <Column header={t('table_headers.last_check')} headerStyle={{textAlign:"left", width:'20%'}}  field="lastModified" body={skipPlaceholder(competitorLastModifiedTemplate)}/>
                {showDiscountType() && <Column header={t('product.discount_type')} field="discount_type" headerStyle={{width: '20%'}}/>}
                <Column header={t('product_detail.price')} headerStyle={{textAlign:"center", width:'14%'}} field="price" body={skipPlaceholder(competitorPriceTemplate)}/>
                {showDiscountRate() && <Column header={t('product.discount_rate')} headerStyle={{textAlign:"center", width:'10%'}} field="discount_rate" body={skipPlaceholder(competitorDiscountRateTemplate)}/>}
                {showShippingPrices() && <Column header={t('product_detail.shipping_price')} headerStyle={{textAlign:"right", width: '14%'}} field="shipping_price" body={skipPlaceholder(competitorShippingPriceTemplate)}/>}
                <Column header={t('product_detail.stock')} headerStyle={{textAlign:"center", width:'9%'}} field="stock" body={skipPlaceholder(competitorStockTemplate)} />
                {isDeliveryTracked(selectedCampaign?.settings) && <Column header={t('product.marketplace.delivery')} headerStyle={{width:'12%'}} body={skipPlaceholder(competitorDeliveryTemplate)} />}
                {isCampaignManufacturer(selectedCampaign) && <Column header={t('price_violation.days')} headerStyle={{textAlign:"center", width:'9%'}} body={skipPlaceholder(violationDaysTemplate)}/>}

                {/*{showReviews() && <Column header={t('product_detail.reviews')} headerStyle={{textAlign:"right", width: '14%'}}  field="reviews" body={competitorReviewsTemplate}/>}*/}
                {/*<Column header={t('table_headers.scraping_status')} className="preventSelect" headerStyle={{textAlign:"right", width:'10.5em'}} body={competitorStatusTemplate} />*/}

                {selectedProduct.pricing_strategy &&
                selectedProduct.pricing_strategy.ignore_some_competitors &&
                <Column header={t('product_detail.repricing')} body={skipPlaceholder(dynamicPricingActiveTemplate)} className="preventSelect" headerStyle={{textAlign:"right", width:'5em'}}/>}
                <Column header={t('table_headers.action')} body={skipPlaceholder(competitorActionTemplate)} className="preventSelect" headerStyle={{textAlign:"left", width:'13em'}}/>
                {/*{!isDesktop() && <Column expander={true} style={{width:'3em'}}/>}*/}
            </DataTable>

            {showBulkOperations &&
            <CompetitorBulkOperations selectedProduct={selectedProduct}
                                      changeCompetitorIsDynamicPricingActive={props.changeCompetitorIsDynamicPricingActive}
                                      changeCompetitorIsActive={props.changeCompetitorIsActive}
                                      deleteCompetitor={props.deleteCompetitor}
                                      hideDialog={() => {setShowBulkOperations(false);}} />
            }

            {showDeleteConfirmation &&
            <Dialog header={t('dialogs.competitor.delete')} footer={modalYesNo} visible={true} style={{maxWidth: '50vw', minWidth: '20vw'}} modal={true} onHide={closeDeleteConfirmation}>
                {
                    ownListing ?
                        <Message severity="info" className="p-text-left"
                                 text={t(canDeactivateSeller ? 'dialogs.competitor.own_listing_msg'
                                                             : 'dialogs.competitor.own_listing_msg_no_action',
                                         {listing: ownListing.product_name || getHostName(ownListing.product_url),
                                          seller: selectedSellerName})}/> :
                        (hasSellerChoice ?
                    <React.Fragment>
                        {deleteListing &&
                        <div className="p-mb-2">
                            <span>{t('dialogs.competitor.belongs_to_listing', {seller: selectedSellerName})} </span>
                            <a href={deleteListing.product_url} target="_blank" rel="noopener noreferrer">
                                <b>{deleteListing.product_name || deleteListing.product_url}</b>
                            </a>
                            <span> ({getHostName(deleteListing.product_url)})</span>
                        </div>}

                        <div className="p-mb-2">
                            <div className="p-field-radiobutton p-mb-1">
                                <RadioButton inputId="deleteModeListing" value="listing" checked={deleteMode === 'listing'}
                                             onChange={() => setDeleteMode('listing')}/>
                                <label htmlFor="deleteModeListing" className="p-ml-2">
                                    {t('dialogs.competitor.delete_listing_option', {count: deleteSellerCount})}
                                </label>
                            </div>
                            <div className="p-field-radiobutton">
                                <RadioButton inputId="deleteModeSeller" value="seller" checked={deleteMode === 'seller'}
                                             onChange={() => setDeleteMode('seller')}/>
                                <label htmlFor="deleteModeSeller" className="p-ml-2">
                                    {t('dialogs.competitor.deactivate_seller_option', {seller: selectedSellerName})}
                                </label>
                            </div>
                        </div>

                        <Message className="p-text-left" severity={deleteMode === 'listing' ? 'warn' : 'info'}
                                 text={deleteMode === 'listing' ? t('dialogs.competitor.delete_listing_hint')
                                                                : t('dialogs.competitor.deactivate_seller_hint')}/>

                        {deleteMode === 'listing' &&
                        <DataTable value={deleteFamily}
                                   responsive={true}
                                   resizableColumns={true} columnResizeMode="fit">
                            <Column body={IndexTemplate} headerStyle={{width:'3em'}} />
                            <Column header={t('table_headers.name')} field="competitorUrl" body={competitorProductUrlTemplate} headerStyle={{width:'45%'}}/>
                            <Column header={t('table_headers.last_check')} headerStyle={{textAlign:"left", width:'25%'}}  field="lastModified" body={competitorLastModifiedTemplate}/>
                            <Column header={t('product_detail.price')} headerStyle={{textAlign:"center", width:'18%'}} field="price" body={competitorPriceTemplate}/>
                            <Column header={t('product_detail.stock')} headerStyle={{textAlign:"center", width:'12%'}} field="stock" body={competitorStockTemplate} />
                        </DataTable>}
                    </React.Fragment>
                    :
                    <Message severity="warn" text={t('dialogs.competitor.delete_msg')}/>)
                }
            </Dialog>}
        </React.Fragment>
    );

};
export default Competitor;
